import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';

/**
 * Menu déroulant compact piloté par un bouton "...".
 *
 * Usage:
 *   <ActionMenu items={[
 *     { label: 'Modifier', icon: Pencil, onClick: () => ... },
 *     { label: 'Désactiver', icon: PauseCircle, onClick: () => ..., variant: 'warning' },
 *     { type: 'separator' },
 *     { label: 'Supprimer', icon: Trash2, onClick: () => ..., variant: 'danger' },
 *   ]} />
 */
const ActionMenu = ({ items = [], align = 'right', testId }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  const toggle = () => {
    if (!open && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const menuWidth = 220;
      // Coordonnées viewport (position: fixed)
      const top = rect.bottom + 4;
      const left =
        align === 'right'
          ? Math.max(8, rect.right - menuWidth)
          : rect.left;
      setPosition({ top, left });
    }
    setOpen(!open);
  };

  const handleItemClick = (onClick) => {
    if (onClick) onClick();
    setOpen(false);
  };

  return (
    <div className="action-menu-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className="action-menu-trigger"
        onClick={toggle}
        title="Actions"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={testId || 'action-menu-trigger'}
      >
        <MoreHorizontal size={18} />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="action-menu"
            role="menu"
            style={{ top: position.top, left: position.left }}
          >
            {items.map((item, idx) => {
              if (item.type === 'separator') {
                return <div key={`sep-${idx}`} className="action-menu-separator" />;
              }
              const Icon = item.icon;
              return (
                <button
                  key={idx}
                  type="button"
                  role="menuitem"
                  className={`action-menu-item ${item.variant ? `action-menu-item-${item.variant}` : ''}`}
                  onClick={() => handleItemClick(item.onClick)}
                  disabled={item.disabled}
                  data-testid={item.testId}
                >
                  {Icon && <Icon size={15} />}
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
};

export default ActionMenu;
